import React, {
  useEffect, useRef,
  useState
} from "react";
import { Stage } from "react-konva";
import {
  Provider,
  useDispatch,
  useSelector,
  useStore
} from "react-redux";
import { Line as LineType, LineConfig } from "konva/types/shapes/Line";

import { openCityDialog, openDialog } from "../../store/actions/ui";
import TurnNews from "./TurnNews/TurnNews";
import { DragescentState } from "../../store/reducers/stateType";
import CityParts from "./CityParts";
import CityLayer from "./CityLayer";
import { SlotType } from "../../lib/types";

// interface ICityProps {
//   scrollX: number;
//   scrollY: number;
// }

type ScaleAttributes = {
  containerWidth: number;
  bgWidth: number;
  bgHeight: number;
  height: number;
}

// sizes based on land bg image
const MAX_LAND_WIDTH = 2220;
const MAX_WIDTH = 1800;
const MAX_HEIGHT = 880;
// const MAX_HEIGHT = 720;
// const topOffset = 148;

const CityMap = () => {
  const calculateScale = () => {
    const cityContainer = document.querySelector(".city-container");
    const containerWidth = (cityContainer && cityContainer.clientWidth) || MAX_WIDTH;
    const bgWidth = (containerWidth * MAX_LAND_WIDTH) / MAX_WIDTH;
    const bgHeight = bgWidth * 0.56;
    // const bgHeight = bgWidth * 0.49;
    const height = Math.round(containerWidth / MAX_WIDTH * MAX_HEIGHT);

    return {
      containerWidth,
      bgWidth,
      bgHeight,
      height
    };
  }

  const slotChains = useSelector((state: DragescentState) => state.governorate.slotChains);
  const showCityDialog = useSelector((state: DragescentState) => state.ui.showCityDialog);
  const slotPrices = useSelector((state: DragescentState) => state.governorate.slotPrices);
  const allBuildings = useSelector((state: DragescentState) => state.governorate.allBuildings);
  const dispatch = useDispatch();

  const [active, setActive] = useState<string[]>([]);
  const [hover, setHover] = useState<boolean>(false);
  const [open, setOpen] = useState<string | null>(null);
  const [scale, setScale] = useState<ScaleAttributes>(calculateScale());
  const newBuildingRef = useRef(null);

  const handleResize = () => {
    setScale(calculateScale());
  }

  useEffect(() => {
    setScale(calculateScale());

    document.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!showCityDialog) {
      setOpen(null);
    }
  }, [showCityDialog]);

  const handleBuildingHoverIn = (key: string) => {
    setActive(active.includes(key) ? active : [...active, key]);
    setHover(true);
  }

  const handleBuildingHoverOut = (key: string) => {
    if (key !== open) {
      const clearedActive = active.slice();
      clearedActive.splice(active.findIndex(a => a === key));
      setActive(clearedActive);
      setHover(false);
    }
  }

  const handleBuildingClick = (buildingElement: LineType<LineConfig> | null, key: string) => {
    setActive(active.includes(key) ? active : [...active, key]);
    setOpen(key);
    setHover(false);
    dispatch(openCityDialog({
      slotChainKey: key,
      buildingElement,
      xOffset: scale.containerWidth > MAX_WIDTH
        ? (scale.containerWidth - MAX_WIDTH) / 2
        : undefined
    }));
  }

  return (
    <section className={`city-container ${hover ? "hover" : ""}`} style={{ height: `${scale.bgHeight}px` }}>
      <div className="city-land"
           style={scale.containerWidth < MAX_WIDTH && scale.bgWidth < MAX_LAND_WIDTH ? {
             backgroundSize: `${scale.bgWidth}px`,
             width: `${scale.bgWidth}px`,
             height: `${scale.bgHeight}px`
           } : { backgroundSize: `${MAX_LAND_WIDTH}px` }}
      />
      {/* <div className="city" style={{ height: `${height}px`,
          top: `${containerWidth / maxWidth * topOffset}px` }}> */}
      <div className="city" style={{
        height: `${scale.height}px`,
        left: scale.containerWidth > MAX_WIDTH
          ? `${(scale.containerWidth - MAX_WIDTH) / 2}px`
          : "0"
      }}>
        {/* {Object.keys(slotChains).map( */}
        {/*  key => <CityPart */}
        {/*    key={key} */}
        {/*    slotChain={slotChains[key]} */}
        {/*    active={active.includes(key)} */}
        {/*  /> */}
        {/* )} */}
        <CityParts active={active} />
      </div>
      <Stage
        width={scale.containerWidth < MAX_WIDTH ? scale.containerWidth : MAX_WIDTH}
        // height={720}
        height={scale.height}
        style={{
          left: scale.containerWidth > MAX_WIDTH
            ? `${(scale.containerWidth - MAX_WIDTH) / 2}px`
            : "0"
        }}
        scale={{
          x: (scale.containerWidth < MAX_WIDTH ? scale.containerWidth / MAX_WIDTH : 1),
          y: (scale.height < MAX_HEIGHT ? scale.height / MAX_HEIGHT : 1)
        }}
        className="city-events"
      >
        {/* <Layer> */}
        {/* {Object.keys(slotChains).map(key => <BuildingLine */}
        {/*   key={`line-${key}`} */}
        {/*   slotKey={key} */}
        {/*   points={buildingLines[slotChains[key].representantName].points} */}
        {/*   buildingHover={handleBuildingHoverIn} */}
        {/*   buildingBlur={handleBuildingHoverOut} */}
        {/*   buildingClick={handleBuildingClick} */}
        {/* />)} */}
        {/* </Layer> */}
        <Provider store={useStore()}>
          <CityLayer
            handleBuildingHoverIn={handleBuildingHoverIn}
            handleBuildingHoverOut={handleBuildingHoverOut}
            handleBuildingClick={handleBuildingClick}
          />
        </Provider>
      </Stage>

      <div className="city-controls">
        <TurnNews />
        <a
          className={"btn btn-black"}
          ref={newBuildingRef}
          onClick={() => dispatch(openDialog({
            name: "NewBuilding",
            classes: ["new-manor"],
            availableBuildings: allBuildings.filter((b: SlotType) => b.id && b.level === 1
              && !Object.keys(slotChains || {}).includes(b.chain.toString(10))
              && !Object.keys(slotChains.workshops || {}).includes(b.chain.toString(10))
              && !(slotChains.guilds && [15, 16, 17, 18, 19, 20].includes(b.chain))
              && !Object.keys(slotChains.guilds || {}).includes(b.chain.toString(10))
              && !(slotChains.manufactories && [29, 30, 31].includes(b.chain))
              && !Object.keys(slotChains.manufactories || {}).includes(b.chain.toString(10))),
            slotPrices,
            elementRef: newBuildingRef.current
          }))}
        >
          {/* <span className="info"> */}
          {/*  <i className="icon icon-dum icon-xlg middle"/> */}
          {/* </span> */}
          Build
        </a>
      </div>
    </section>);
}

export default CityMap;
